import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { env } from '../../shared/config/env';
import type { AppSession, Role } from '../../shared/types/domain';
import type { AuthGateway } from './auth.types';
import { demoAuth } from './demoAuth';
import { supabaseAuth } from './supabaseAuth';

type AuthStatus = 'loading' | 'anonymous' | 'authenticated';

interface AuthContextValue {
  status: AuthStatus;
  session: AppSession | null;
  mode: 'demo' | 'supabase';
  error: string;
  signInWithPassword(phone: string, password: string): Promise<void>;
  signInAsDemo(role: Role): Promise<void>;
  signOut(): Promise<void>;
  clearError(): void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children, gateway }: { children: ReactNode; gateway?: AuthGateway }) {
  const mode = env.VITE_AUTH_MODE;
  const authGateway = gateway ?? (mode === 'supabase' ? supabaseAuth : demoAuth);
  const [session, setSession] = useState<AppSession | null>(null);
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    authGateway.restore()
      .then((next) => {
        if (!active) return;
        if (next && isSessionExpired(next)) {
          void authGateway.signOut();
          setSession(null);
          setStatus('anonymous');
          return;
        }
        setSession(next);
        setStatus(next ? 'authenticated' : 'anonymous');
      })
      .catch((cause: unknown) => {
        if (!active) return;
        setError(cause instanceof Error ? cause.message : '恢复登录状态失败。');
        setStatus('anonymous');
      });
    return () => { active = false; };
  }, [authGateway]);

  useEffect(() => {
    if (!session?.expiresAt) return;
    const remaining = session.expiresAt * 1000 - Date.now();
    if (remaining <= 0) {
      void authGateway.signOut().finally(() => { setSession(null); setStatus('anonymous'); });
      return;
    }
    const timer = window.setTimeout(() => {
      void authGateway.signOut().finally(() => { setSession(null); setStatus('anonymous'); });
    }, Math.min(remaining, 2_147_483_647));
    return () => window.clearTimeout(timer);
  }, [authGateway, session]);

  const perform = useCallback(async (action: () => Promise<AppSession | void>) => {
    setError('');
    try {
      const next = await action();
      if (next) {
        setSession(next);
        setStatus('authenticated');
      }
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : '认证操作失败。';
      setError(message);
      throw cause;
    }
  }, []);

  const value = useMemo<AuthContextValue>(() => ({
    status,
    session,
    mode,
    error,
    signInWithPassword: (phone, password) => perform(() => authGateway.signInWithPassword(phone, password)),
    signInAsDemo: (role) => perform(() => authGateway.signInAsDemo(role)),
    signOut: async () => {
      try {
        await authGateway.signOut();
      } finally {
        setSession(null);
        setStatus('anonymous');
      }
    },
    clearError: () => setError(''),
  }), [authGateway, error, mode, perform, session, status]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function isSessionExpired(session: AppSession, now = Date.now()) {
  return session.expiresAt !== null && session.expiresAt * 1000 <= now;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth必须在AuthProvider内使用。');
  return context;
}
